import { z } from 'zod';

/**
 * Schema for print data fields used in PDF génération – arbre
 * Cette forme correspond aux inventaires de type ARBRE / ALIGNEMENT.
 */
export const ArbrePrintDataFieldsSchema = z.object({
  organization: z.any(),
  organizationAddress: z.string(),
  organizationCity: z.string(),
  organizationZipCode: z.string(),
  organizationSiret: z.string(),
  organizationNumCertification: z.string(),
  organizationLogo: z.string().nullable(),
  organizationName: z.string(),
  user: z.any().optional(),
  inventaireType: z.string().optional(),
  headerTitle: z.string(),
  formattedDate: z.string(),
  addressLine: z.string(),
  code_site: z.string(),
  num_sujet: z.string(),
  gps: z.string(),
  especeLabel: z.string(),
  espece: z.any().nullable().optional(),
  genreLatin: z.string(),
  especeLatin: z.string(),
  cultivar: z.string(),
  diametre: z.string(),
  hauteur: z.string(),
  caracteristiques: z.object({
    stade_dev: z.string(),
    port_arbre: z.string(),
    caract_tronc: z.string(),
    caract_pied: z.string(),
    critere: z.string(),
    implantation: z.string(),
    domaine: z.string(),
    situation: z.string(),
    accessibilite: z.string(),
  }),
  etatSanitaire: z.object({
    collet: z.string(),
    tronc: z.string(),
    houppier: z.string(),
    general: z.string(),
    general_complementaire: z.boolean(),
  }),
  defautsMecaniques: z.object({
    collet: z.string(),
    tronc: z.string(),
    houppier: z.string(),
    champignons_collet: z.string().optional(),
    champignons_tronc: z.string().optional(),
    champignons_houppier: z.string().optional(),
    nuisibles_collet: z.string().optional(),
    nuisibles_tronc: z.string().optional(),
    nuisibles_houppier: z.string().optional(),
  }),
  environnement: z.object({
    proximite: z.string(),
    dict: z.string(),
    nuisance: z.string(),
    taux_freq: z.string(),
    type_passage: z.string(),
    accessibilite: z.string(),
  }),
  risque: z.object({
    collet: z.string(),
    tronc: z.string(),
    houppier: z.string(),
    general: z.string(),
  }),
  travaux: z.object({
    abattage: z.string(),
    travaux_collet: z.string(),
    travaux_tronc: z.string(),
    travaux_houppier: z.string(),
    date_travaux: z.string(),
    date_pro_visite: z.string(),
  }),
  img1: z.string().nullable(),
  img2: z.string().nullable(),
  img3: z.string().nullable(),
  comments: z.string(),
  beva: z.string(),
  bevaDetail: z
    .object({
      numero: z.string(),
      genre: z.string(),
      espece: z.string(),
      nomVernaculaire: z.string(),
      varietyGrade: z.number(),
      aestheticHealthIndex: z.number(),
      locationIndex: z.number(),
      sizeIndex: z.number(),
      beva: z.number(),
    })
    .nullable()
    .optional(),
  dictRequired: z.string(),
  mappedData: z.any().optional(),
  resolver: z.any().optional(),
});

/**
 * Schema pour les données d'impression épaysage (Espace boisé).
 * Ce format correspond à la réponse de `prepareEpaysagePrintData`.
 */
export const EpaysagePrintDataFieldsSchema = z.object({
  organization: z.any().optional(),
  organizationAddress: z.string(),
  organizationCity: z.string(),
  organizationZipCode: z.string(),
  organizationSiret: z.string(),
  organizationNumCertification: z.string(),
  organizationLogo: z.string().nullable(),
  organizationName: z.string(),
  user: z.any().optional(),
  headerTitle: z.string(),
  formattedDate: z.string(),
  addressLine: z.string(),
  code_site: z.string(),
  num_sujet: z.string(),
  gps: z.string(),
  type_espace: z.string(),
  surface: z.string(),
  essence: z.array(z.any()),
  essences: z.array(z.string()),
  essences1: z.any().optional(),
  essences2: z.any().optional(),
  essences3: z.any().optional(),
  essences4: z.any().optional(),
  essences5: z.any().optional(),
  totalSubjects: z.string(),
  caracteristiques: z.object({
    milieu: z.string(),
    sol: z.string(),
    domaine: z.string(),
    proximite: z.string(),
    accessibilite: z.string(),
    taux_freq: z.string(),
    type_passage: z.string(),
    age_estime: z.string(),
  }),
  environnement: z.object({
    nuisance: z.string(),
    situation: z.string(),
  }),
  risque: z.object({
    general: z.string(),
  }),
  img1: z.string().nullable(),
  img2: z.string().nullable(),
  img3: z.string().nullable(),
  comments: z.string(),
});

/**
 * Schéma unifié pour les données d'impression retournées par l'API `/inventaires/print-data/:id`.
 * Peut représenter soit un arbre, soit un espace boisé.
 */
export const PrintDataFieldsSchema = z.union([
  ArbrePrintDataFieldsSchema,
  EpaysagePrintDataFieldsSchema,
]);

export const PrintDataResponseSchema = z.object({
  data: PrintDataFieldsSchema,
  message: z.string().optional(),
});

export type PrintDataFields = z.infer<typeof PrintDataFieldsSchema>;
export type PrintDataResponse = z.infer<typeof PrintDataResponseSchema>;
